import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import type { DateRange } from "../DateRangePicker";

export interface EntityMonthlyData {
  entityId: number | string;
  entityName: string;
  color: string;
  sessions: { date: Date }[];
}

interface SessionsPerMonthComparisonChartProps {
  entities: EntityMonthlyData[];
  dateRange: DateRange;
}

const SessionsPerMonthComparisonChart = ({
  entities,
  dateRange,
}: SessionsPerMonthComparisonChartProps) => {
  const { t } = useTranslation();

  // State for the end date of the 12-month view window
  const [viewEndDate, setViewEndDate] = useState(new Date());

  // Reset view when dateRange changes
  useEffect(() => {
    setViewEndDate(new Date());
  }, [dateRange]);

  const monthLabels = useMemo(
    () => [
      t("pages.statistics.months.january"),
      t("pages.statistics.months.february"),
      t("pages.statistics.months.march"),
      t("pages.statistics.months.april"),
      t("pages.statistics.months.may"),
      t("pages.statistics.months.june"),
      t("pages.statistics.months.july"),
      t("pages.statistics.months.august"),
      t("pages.statistics.months.september"),
      t("pages.statistics.months.october"),
      t("pages.statistics.months.november"),
      t("pages.statistics.months.december"),
    ],
    [t],
  );

  const chartData = useMemo(() => {
    // Generate 12 months ending at viewEndDate
    const months: Array<{
      month: number;
      year: number;
      label: string;
      [key: string]: number | string;
    }> = [];

    for (let i = 11; i >= 0; i--) {
      const date = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() - i, 1);
      const dataPoint: {
        month: number;
        year: number;
        label: string;
        [key: string]: number | string;
      } = {
        month: date.getMonth(),
        year: date.getFullYear(),
        label: `${monthLabels[date.getMonth()].slice(0, 3)} ${date
          .getFullYear()
          .toString()
          .slice(-2)}`,
      };

      // Initialize count for each entity
      entities.forEach((entity) => {
        dataPoint[`entity_${entity.entityId}`] = 0;
      });

      months.push(dataPoint);
    }

    // Count sessions per month per entity
    entities.forEach((entity) => {
      entity.sessions.forEach((session) => {
        const sessionMonth = session.date.getMonth();
        const sessionYear = session.date.getFullYear();

        const monthData = months.find((m) => m.month === sessionMonth && m.year === sessionYear);
        if (monthData) {
          const key = `entity_${entity.entityId}`;
          monthData[key] = (monthData[key] as number) + 1;
        }
      });
    });

    return months;
  }, [entities, monthLabels, viewEndDate]);

  const hasData = entities.some((e) => e.sessions.length > 0);

  // Navigation handlers
  const goToPrevMonth = () => {
    setViewEndDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    const next = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() + 1, 1);
    if (next <= new Date()) {
      setViewEndDate(next);
    }
  };

  const goToPrevYear = () => {
    setViewEndDate((prev) => new Date(prev.getFullYear() - 1, prev.getMonth(), 1));
  };

  const goToNextYear = () => {
    const next = new Date(viewEndDate.getFullYear() + 1, viewEndDate.getMonth(), 1);
    if (next <= new Date()) {
      setViewEndDate(next);
    }
  };

  const canGoNext =
    new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() + 1, 1) <= new Date();

  // Get display period text
  const periodText = useMemo(() => {
    const startDate = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() - 11, 1);
    const startStr = `${monthLabels[startDate.getMonth()].slice(0, 3)} ${startDate.getFullYear()}`;
    const endStr = `${monthLabels[viewEndDate.getMonth()].slice(
      0,
      3,
    )} ${viewEndDate.getFullYear()}`;
    return `${startStr} - ${endStr}`;
  }, [viewEndDate, monthLabels]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p className="font-medium text-gray-900 mb-2">{label}</p>
        {payload.map((entry: any, index: number) => {
          const entity = entities.find((e) => `entity_${e.entityId}` === entry.dataKey);
          return (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.fill }} />
              <span className="text-gray-600">{entity?.entityName}:</span>
              <span className="font-medium">{entry.value}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Navigation controls */}
      <div className="flex-shrink-0 flex items-center justify-between mb-2">
        <div className="flex items-center gap-1">
          {/* Previous year */}
          <button
            onClick={goToPrevYear}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title={t("statsNew.charts.prevYear")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
              />
            </svg>
          </button>
          {/* Previous month */}
          <button
            onClick={goToPrevMonth}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title={t("statsNew.charts.prevMonth")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        </div>

        <span className="text-sm text-gray-500 font-medium">{periodText}</span>

        <div className="flex items-center gap-1">
          {/* Next month */}
          <button
            onClick={goToNextMonth}
            disabled={!canGoNext}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={t("statsNew.charts.nextMonth")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {/* Next year */}
          <button
            onClick={goToNextYear}
            disabled={!canGoNext}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={t("statsNew.charts.nextYear")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 5l7 7-7 7M5 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Chart */}
      {!hasData ? (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          {t("statsNew.charts.noData")}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%" className="flex-1">
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              allowDecimals={false}
              width={30}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: "10px" }}
              formatter={(value: string) => {
                const entityId = value.replace("entity_", "");
                const entity = entities.find((e) => String(e.entityId) === entityId);
                return <span className="text-sm text-gray-700">{entity?.entityName || value}</span>;
              }}
            />
            {entities.map((entity) => (
              <Bar
                key={entity.entityId}
                dataKey={`entity_${entity.entityId}`}
                fill={entity.color}
                radius={[4, 4, 0, 0]}
                maxBarSize={30}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default SessionsPerMonthComparisonChart;
